import get from 'lodash/get';
import { IStateCalendar, IStateTable } from 'services/types/apt';
import { RootState } from 'store';
import { useAppSelector } from 'store/hooks';
type MyState = RootState['apt'];

const getCurrentState = (state: RootState): MyState => state['apt'];

const selector = <T = MyState>(key: keyof T, defaultValue?: any) => useAppSelector(state => get(getCurrentState(state), key, defaultValue));

export const calendarSelectors = {
  distanceTimeline: () => selector('calendar.distanceTimeLine') as IStateCalendar['distanceTimeLine'],
  typeView: () => selector('calendar.typeView') as IStateCalendar['typeView'],
  dateRanges: () => selector('calendar.dateRanges') as IStateCalendar['dateRanges'],
  data: () => {
    const calendar = selector('calendar') as IStateCalendar;
    return [...calendar.data, ...calendar.blockHours];
  },
  // Referentially-stable slices so consumers can memoize the merge themselves and
  // avoid recomputing on every render (the combined `data()` returns a fresh array each call).
  appointments: () => selector('calendar.data') as IStateCalendar['data'],
  blockHours: () => selector('calendar.blockHours') as IStateCalendar['blockHours'],
  params: () => selector('calendar.params') as IStateCalendar['params'],
  activeTimeRow: () => selector('activeTimeRow') as MyState['activeTimeRow'],
  currentPage: () => selector('currentPage') as MyState['currentPage'],
  fullScreen: () => selector('fullScreen') as MyState['fullScreen'],
};

export const tableSelectors = {
  getPureParams: (state: RootState) => state['apt']?.table?.params || {},
  data: () => selector('table.data') as IStateTable['data'],
  blockHours: () => selector('table.blockHours') as IStateTable['blockHours'],
  params: () => selector('table.params') as IStateTable['params'],
  total: () => selector('table.total') as IStateTable['total'],
};

export const customerSelectors = {
  getPureParams: (state: RootState) => state['apt']?.customer?.params || {},
  data: () => selector('customer.data') as IStateTable['data'],
  params: () => selector('customer.params') as IStateTable['params'],
  total: () => selector('customer.total') as IStateTable['total'],
};
const getShopSurveyInfo = () => selector('shopSurveyInfo') as MyState['shopSurveyInfo'];
const getCustomerTransactions = () => selector('customerTransactions') as MyState['customerTransactions'];

const quickBooking = {
  services: () => selector('quickBooking.services') as MyState['quickBooking']['services'],
};
const staffAvailableBooking = () => selector('staffAvailableBooking') as MyState['staffAvailableBooking'];
const staffAvailableBookingLoading = () => selector('staffAvailableBookingLoading') as MyState['staffAvailableBookingLoading'];
const getAppointmentSummaryCallerId = () => selector('appointmentSummaryCallerId') as IStateTable['appointmentSummaryCallerId'];


const callerId = {
  loading: () => selector('callerId.loading') as MyState['callerId']['loading'],
  data: () => selector('callerId.data') as MyState['callerId']['data'],
  params: () => selector('callerId.params') as MyState['callerId']['params'],
};

const pendingCallers = {
  loading: () => selector('pendingCallers.loading') as MyState['pendingCallers']['loading'],
  data: () => selector('pendingCallers.data') as MyState['pendingCallers']['data'],
};

const customerRecent = {
  loading: () => selector('customerRecent.loading') as MyState['customerRecent']['loading'],
  data: () => selector('customerRecent.data') as MyState['customerRecent']['data'],
};

const customerAppts = {
  loading: () => selector('customerAppts.loading') as MyState['customerAppts']['loading'],
  data: () => selector('customerAppts.data') as MyState['customerAppts']['data'],
  total: () => selector('customerAppts.total') as MyState['customerAppts']['total'],
  page: () => selector('customerAppts.page') as MyState['customerAppts']['page'],
  pageSize: () => selector('customerAppts.pageSize') as MyState['customerAppts']['pageSize'],
};

const aptSelectors = {
  calendar: calendarSelectors,
  table: tableSelectors,
  customer: customerSelectors,
  quickBooking,
  staffAvailableBooking,
  staffAvailableBookingLoading,
  getAppointmentSummaryCallerId,
  callerId,
  pendingCallers,
  customerRecent,
  customerAppts,
  getShopSurveyInfo,
  getCustomerTransactions
};
export default aptSelectors;
